import {
  Box,
  Checkbox as MuiCheckbox,
  FormControlLabel,
  styled,
} from "@mui/material";
import DOMPurify from "dompurify";
import { Colors } from "../../../../Themes/colors";
import { useState } from "react";

export interface NorlysCheckboxProps {
  /** Plain text or ReactNode label */
  label?: string | React.ReactNode;
  /** HTML string label - rendered with dangerouslySetInnerHTML */
  htmlLabel?: string;
  onChange: (checked: boolean) => void;
  checked?: boolean;
  disabled?: boolean;
  id?: string;
  labelFontSize?: string | number;
}

const UncheckedIcon = () => (
  <Box
    sx={{
      width: 32,
      height: 32,
      borderRadius: "6px",
      border: `2px solid ${Colors.grey30}`,
      backgroundColor: Colors.white,
      boxSizing: "border-box",
    }}
  />
);

const CheckedIcon = () => (
  <Box
    sx={{
      width: 32,
      height: 32,
      borderRadius: "6px",
      border: `2px solid ${Colors.greenStrong}`,
      backgroundColor: Colors.greenStrong,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxSizing: "border-box",
    }}
  >
    <svg
      width="18"
      height="14"
      viewBox="0 0 18 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M1.5 7L6.5 12L16.5 2"
        stroke="white"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </Box>
);

export const NorlysCheckbox: React.FC<NorlysCheckboxProps> = ({
  label,
  htmlLabel,
  onChange,
  checked,
  disabled,
  id,
  labelFontSize = "18px",
}) => {
  const isControlled = checked !== undefined;
  const [internalChecked, setInternalChecked] = useState(false);
  const isChecked = isControlled ? checked : internalChecked;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    if (!isControlled) {
      setInternalChecked(e.target.checked);
    }
    onChange(e.target.checked);
  };

  const renderLabel = () => {
    if (htmlLabel) {
      return (
        <Box
          component="span"
          sx={{
            fontFamily: "NORLYSText-Regular",
            fontSize: labelFontSize,
            lineHeight: 1.4,
            "& a": { color: "inherit", textDecoration: "underline" },
          }}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(htmlLabel) }}
        />
      );
    }
    return (
      <Box
        component="span"
        sx={{
          fontFamily: "NORLYSText-Regular",
          fontSize: labelFontSize,
          lineHeight: 1.4,
        }}
      >
        {label}
      </Box>
    );
  };

  return (
    <StyledFormControlLabel
      control={
        <StyledCheckbox
          checked={isChecked}
          onChange={handleChange}
          disabled={disabled}
          id={id}
          icon={<UncheckedIcon />}
          checkedIcon={<CheckedIcon />}
        />
      }
      label={renderLabel()}
    />
  );
};

const StyledCheckbox = styled(MuiCheckbox)`
  padding: 0;
  margin-right: 16px;
  flex-shrink: 0;
  &:hover {
    background-color: transparent;
  }
  .MuiTouchRipple-root {
    border-radius: 6px;
  }
`;

const StyledFormControlLabel = styled(FormControlLabel)`
  align-items: flex-start;
  margin-left: 0;
  max-width: 600px;
  cursor: pointer;
`;
